import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { subscribeToHousehold } from '../ws';
import { useAuth } from './AuthContext';

const HouseholdContext = createContext(null);

export function HouseholdProvider({ children }) {
  const { user } = useAuth();
  const [households, setHouseholds]     = useState([]);
  const [activeId, setActiveId]         = useState(api.getActiveHousehold());
  const [loading, setLoading]           = useState(false);

  const loadHouseholds = useCallback(async (showLoading = false) => {
    if (!user) return;
    if (showLoading) setLoading(true);
    try {
      const list = await api.getHouseholds();
      setHouseholds(list);

      // Hvis ingen aktiv husstand valgt, vælg den første
      const current = api.getActiveHousehold();
      const valid = list.find(h => h.id === current);
      if (!valid && list.length > 0) {
        api.setActiveHousehold(list[0].id);
        setActiveId(list[0].id);
      } else if (valid) {
        setActiveId(current);
      }
    } catch (err) {
      console.error('Kunne ikke hente husstande:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadHouseholds(true); }, [loadHouseholds]);

  const switchHousehold = (id) => {
    api.setActiveHousehold(id);
    setActiveId(id);
    subscribeToHousehold(id);
  };

  const activeHousehold = households.find(h => h.id === activeId) || null;

  const addHousehold = (hh) => {
    setHouseholds(prev => [...prev, hh]);
    switchHousehold(hh.id);
  };

  const updateHousehold = (id, updates) =>
    setHouseholds(prev => prev.map(h => h.id === id ? { ...h, ...updates } : h));

  const removeHousehold = (id) => {
    setHouseholds(prev => {
      const next = prev.filter(h => h.id !== id);
      if (activeId === id && next.length > 0) switchHousehold(next[0].id);
      return next;
    });
  };

  return (
    <HouseholdContext.Provider value={{
      households, activeHousehold, activeId,
      loading, switchHousehold, reload: loadHouseholds,
      addHousehold, updateHousehold, removeHousehold,
    }}>
      {children}
    </HouseholdContext.Provider>
  );
}

export const useHousehold = () => {
  const ctx = useContext(HouseholdContext);
  if (!ctx) throw new Error('useHousehold skal bruges inden i HouseholdProvider');
  return ctx;
};
